import { defaultSetTimer, type SetTimer } from "./timers";

export const TOKEN_TIMEOUT_MS = 5_000;

export interface TokenRelay {
  /** Asks every client for a token and resolves with the first answer. */
  request: () => Promise<string>;
  provide: (token: string) => void;
}

export interface CreateTokenRelayOptions {
  requestFromClients: () => void;
  setTimer?: SetTimer;
}

interface Pending {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
  cancelTimeout: () => void;
}

export const createTokenRelay = ({
  requestFromClients,
  setTimer = defaultSetTimer,
}: CreateTokenRelayOptions): TokenRelay => {
  let pending: Pending | undefined;

  const settle = (): Pending | undefined => {
    const current = pending;
    pending = undefined;
    current?.cancelTimeout();
    return current;
  };

  const request = (): Promise<string> => {
    if (pending !== undefined) {
      return new Promise<string>((resolve, reject) => {
        const current = pending;
        if (current === undefined) {
          reject(new Error("token request settled"));
          return;
        }
        const previousResolve = current.resolve;
        const previousReject = current.reject;
        current.resolve = (token) => {
          previousResolve(token);
          resolve(token);
        };
        current.reject = (error) => {
          previousReject(error);
          reject(error);
        };
      });
    }

    return new Promise<string>((resolve, reject) => {
      const cancelTimeout = setTimer(TOKEN_TIMEOUT_MS, () => {
        settle()?.reject(new Error("no window answered the token request"));
      });
      pending = { resolve, reject, cancelTimeout };
      requestFromClients();
    });
  };

  const provide = (token: string) => {
    settle()?.resolve(token);
  };

  return {
    request,
    provide,
  };
};
